import React, { useEffect, useState, Suspense, lazy, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { HelmetProvider, Helmet } from "react-helmet-async";

import Header from "./components/Header";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./contexts/AuthContext";
import { SearchProvider } from "./contexts/SearchContext";
import Footer from "./components/Footer";
import ErrorBoundary from "./components/ErrorBoundary";

// Lazy load page components
const HomePage = lazy(() => import("./pages/HomePage"));
const CategoriesPage = lazy(() => import("./pages/CategoriesPage"));
const GameDetails = lazy(() => import("./pages/GameDetails"));
const FavoritesPage = lazy(() => import("./pages/FavoritesPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const RedirectPage = lazy(() => import("./pages/RedirectPage"));
const GOTYGamePage = lazy(() => import("./pages/GOTYGamePage"));
const GOTYPage = lazy(() => import("./pages/GOTYPage"));

const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
    <span className="ml-3 text-gray-600 dark:text-gray-300">Loading...</span>
  </div>
);

// Google Analytics
const GA_MEASUREMENT_ID = "G-2JJK8FNTLS";

const initializeGA = () => {
  if (!window.gtag) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.id = "google-analytics-script";
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID, {
      page_path: window.location.pathname,
    });
  }
};

const logPageView = (url) => {
  if (window.gtag) {
    window.gtag("event", "page_view", { page_path: url });
  }
};

const PageWrapper = React.memo(({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -30 }}
    transition={{ duration: 0.35 }}
  >
    {children}
  </motion.div>
));

PageWrapper.displayName = "PageWrapper";

const AnimatedRoutes = () => {
  const location = useLocation();

  useEffect(() => {
    logPageView(location.pathname + location.search);

    if (window.gtag) {
      if (location.pathname === "/") {
        window.gtag("event", "view_goty_section", {
          event_category: "Engagement",
          event_label: "Homepage GOTY Section",
        });
      } else if (location.pathname.startsWith("/game/goty/")) {
        const gameSlug = location.pathname.split("/").pop();
        window.gtag("event", "view_goty_game", {
          event_category: "Games",
          event_label: gameSlug,
        });
      } else if (location.pathname === "/goty") {
        window.gtag("event", "view_goty_collection", {
          event_category: "Pages",
          event_label: "GOTY Collection Page",
        });
      }
    }
  }, [location]);

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageWrapper><HomePage /></PageWrapper>} />
        <Route path="/categories" element={<PageWrapper><CategoriesPage /></PageWrapper>} />
        <Route path="/goty" element={<PageWrapper><GOTYPage /></PageWrapper>} />
        <Route path="/game/:name" element={<PageWrapper><GameDetails /></PageWrapper>} />
        <Route path="/game/goty/:slug" element={<PageWrapper><GOTYGamePage /></PageWrapper>} />
        <Route path="/redirect" element={<PageWrapper><RedirectPage /></PageWrapper>} />
        <Route path="/favorites" element={<PageWrapper><ProtectedRoute><FavoritesPage /></ProtectedRoute></PageWrapper>} />
        <Route path="/account" element={<PageWrapper><AccountPage /></PageWrapper>} />
        <Route path="/about" element={<PageWrapper><AboutPage /></PageWrapper>} />
        <Route path="*" element={<PageWrapper><NotFound /></PageWrapper>} />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  const [dark, setDark] = useState(() => localStorage.getItem("darkMode") === "true");

  const toggleDark = useCallback(() => {
    setDark(prevDark => {
      const newDark = !prevDark;
      if (window.gtag) {
        window.gtag("event", "toggle_dark_mode", {
          event_category: "UI",
          event_label: newDark ? "enabled" : "disabled",
        });
      }
      return newDark;
    });
  }, []);

  useEffect(() => {
    initializeGA();
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("darkMode", dark);
  }, [dark]);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <SearchProvider>
            <Router basename="/">
              <Suspense fallback={<LoadingSpinner />}>
                <ScrollToTop />
                <Header toggleDark={toggleDark} isDark={dark} />
                <main className="bg-white text-gray-900 dark:bg-gray-950 dark:text-white min-h-screen pt-16">
                  <AnimatedRoutes />
                </main>
                <Footer />
              </Suspense>
            </Router>
          </SearchProvider>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;